import { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Bandeau, Liste, Zone } from './Champs'
import { dateCourte } from './Messagerie'
import { ProposerVisite } from './Visites'
import { jour, montant } from './Bail'
import { ErreurApi } from '../api'
import {
  accepter,
  candidater,
  candidaturesRecues,
  mesCandidatures,
  preselectionner,
  refuser,
  retirerCandidature,
  toutesCandidaturesRecues,
  type Candidature,
  type StatutCandidature,
  type TrancheRevenu,
  type TypeGarant,
} from '../apiRelations'
import { TEXTES, interpoler, messageErreur, type Langue } from '../i18n'
import { Bouton, EtatVide, Icone, Image, LienBouton, Modale, Onglets, Puce, SqueletteListe } from '../ui'

/**
 * Candidatures — revu le 15/09/2026 sur la maquette `docs/ui/pwa/mes-candidatures.html`.
 *
 * <b>Deux onglets, les deux faces d'un même objet</b> : ce que je dépose, ce que je reçois. Un
 * propriétaire qui cherche aussi un logement les a côte à côte, et un onglet vide ne s'affiche pas.
 *
 * <b>Côté candidat, une carte par candidature</b> — la vignette et le titre de l'annonce, le loyer,
 * la date de dépôt, l'état — et, tant qu'elle est ouverte, <b>le fil d'étapes</b> : déposée, vote du
 * foyer, visite, décision. C'est le motif de progression du système, celui de l'assistant de
 * publication et du cycle d'avis.
 *
 * <b>Côté propriétaire, un tableau dense</b> sur toutes les annonces : le candidat, l'annonce, la
 * tranche de revenu et la nature du garant — jamais un montant, jamais une identité, jamais un
 * document (invariant 8) —, le score du moteur, l'état, l'action. Un refus porte toujours un motif
 * choisi dans une liste fermée : c'est le code qui rend un refus lisible en réputation (L8).
 *
 * <b>L'acceptation ne révèle pas les coordonnées</b> — c'est la confirmation d'une visite qui le
 * fait. Deux gestes, deux moments.
 */
const MOTIFS = ['already_rented', 'budget', 'dates', 'other'] as const

type Motif = (typeof MOTIFS)[number]
type Onglet = 'envoyees' | 'recues'
type Textes = (typeof TEXTES)['fr']

export function MesCandidatures({ langue }: { langue: Langue }) {
  const t = TEXTES[langue]

  const [envoyees, setEnvoyees] = useState<Candidature[] | null>(null)
  const [recues, setRecues] = useState<Candidature[] | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)
  const [onglet, setOnglet] = useState<Onglet | null>(null)

  const charger = useCallback(() => {
    setErreur(null)

    void Promise.all([mesCandidatures(1, 50), toutesCandidaturesRecues()])
      .then(([mes, autres]) => {
        setEnvoyees(mes.items)
        setRecues(autres.items)
        // L'onglet qui a quelque chose à montrer s'ouvre en premier ; le candidat avant le
        // propriétaire, parce que c'est l'attente la plus fréquente.
        setOnglet((courant) => courant ?? (mes.items.length === 0 && autres.items.length > 0 ? 'recues' : 'envoyees'))
      })
      .catch((cause: unknown) => {
        setEnvoyees([])
        setRecues([])
        setErreur(messageErreur(langue, cause instanceof ErreurApi ? cause.code : 'internal'))
      })
  }, [langue])

  useEffect(charger, [charger])

  const chargement = envoyees === null || recues === null
  const actif: Onglet = onglet ?? 'envoyees'
  const ouvertes = (recues ?? []).filter((candidature) => estOuverte(candidature.status)).length

  return (
    <section className="candidatures">
      <div className="page-entete">
        <h2>{t.candidaturesTitre}</h2>
        {!chargement && (envoyees.length > 0 || recues.length > 0) && (
          <Onglets<Onglet>
            ariaLabel={t.candidaturesTitre}
            actif={actif}
            onChange={setOnglet}
            onglets={[
              {
                cle: 'envoyees',
                libelle: (
                  <>
                    {t.mesCandidatures}
                    {envoyees.length > 0 && <Puce>{envoyees.length}</Puce>}
                  </>
                ),
              },
              {
                cle: 'recues',
                libelle: (
                  <>
                    {t.candidaturesRecues}
                    {ouvertes > 0 && <Puce ton="marque">{ouvertes}</Puce>}
                  </>
                ),
              },
            ]}
          />
        )}
      </div>

      {erreur !== null && <Bandeau ton="erreur">{erreur}</Bandeau>}
      {chargement && <SqueletteListe />}

      {!chargement && envoyees.length === 0 && recues.length === 0 && erreur === null && (
        <EtatVide
          icone="fichier"
          titre={t.aucuneCandidature}
          action={
            <LienBouton to="/recherche" apparence="secondaire">
              {t.rechercheChercher}
            </LienBouton>
          }
        >
          {t.candidaturesVideAide}
        </EtatVide>
      )}

      {!chargement && actif === 'envoyees' && envoyees.length > 0 && (
        <Envoyees t={t} langue={langue} liste={envoyees} onChange={charger} onErreur={setErreur} />
      )}

      {!chargement && actif === 'recues' && (recues.length > 0 || envoyees.length > 0) && (
        <Recues t={t} langue={langue} liste={recues} onChange={charger} onErreur={setErreur} />
      )}
    </section>
  )
}

// ---------------------------------------------------------------------------
// Côté candidat
// ---------------------------------------------------------------------------

function Envoyees({
  t,
  langue,
  liste,
  onChange,
  onErreur,
}: {
  t: Textes
  langue: Langue
  liste: Candidature[]
  onChange: () => void
  onErreur: (message: string) => void
}) {
  const [visiteOuverte, setVisiteOuverte] = useState<string | null>(null)
  const [occupe, setOccupe] = useState<string | null>(null)

  const retirer = async (id: string) => {
    setOccupe(id)

    try {
      await retirerCandidature(id)
      onChange()
    } catch (cause: unknown) {
      onErreur(messageErreur(langue, cause instanceof ErreurApi ? cause.code : 'internal'))
    } finally {
      setOccupe(null)
    }
  }

  return (
    <ul className="candidatures__liste">
      {liste.map((candidature) => {
        const ouverte = estOuverte(candidature.status)
        const close = !ouverte && candidature.status !== 'Accepted' && candidature.status !== 'ConvertedToLease'

        return (
          <li key={candidature.id} className={close ? 'bloc bloc--serre candidature est-close' : 'bloc bloc--serre candidature'}>
            <div className="candidature__entete">
              <Image
                mediaId={candidature.listingCoverMediaId}
                alt=""
                ratio="4-3"
                sizes="9rem"
                className="candidature__vignette"
                vide={<Icone nom="maison" taille="lg" />}
              />
              <div className="candidature__corps">
                <div className="rangee rangee--espace rangee--haut">
                  <h3>
                    {candidature.listingId !== null ? (
                      <Link to={`/annonces/${candidature.listingId}`}>{candidature.listingTitle ?? '—'}</Link>
                    ) : (
                      (candidature.listingTitle ?? '—')
                    )}
                  </h3>
                  <Puce ton={tonStatut(candidature.status)}>{libelleStatut(t, candidature.status)}</Puce>
                </div>

                <p className="secondaire">
                  {candidature.listingRent !== null && (
                    <>
                      <bdi>{montant(langue, candidature.listingRent, candidature.listingCurrencyCode ?? 'MAD')}</bdi>
                      {` ${t.annonceParMois} · `}
                    </>
                  )}
                  {candidature.submittedAt !== null
                    ? interpoler(t.candidatureDeposeeLe, { date: dateCourte(langue, candidature.submittedAt) })
                    : libelleStatut(t, candidature.status)}
                </p>

                {ouverte && <Etapes t={t} statut={candidature.status} />}

                {candidature.status === 'UnderHouseholdVote' && (
                  <p className="tertiaire">{t.candidatureVoteAide}</p>
                )}

                {candidature.status === 'Accepted' && (
                  <p className="statut-point statut-point--succes">
                    {candidature.decidedAt !== null
                      ? interpoler(t.candidatureAccepteeLe, { date: jour(langue, candidature.decidedAt) })
                      : t.statutAccepted}
                  </p>
                )}

                {candidature.status === 'Rejected' && (
                  <p className="statut-point statut-point--danger">
                    {interpoler(t.candidatureMotif, { motif: libelleMotif(t, candidature.rejectionReasonCode) })}
                  </p>
                )}

                {candidature.status === 'ConvertedToLease' && (
                  <p className="statut-point statut-point--succes">{t.candidatureBailSigne}</p>
                )}

                <div className="rangee rangee--serree candidature__actions">
                  {candidature.listingId !== null && (ouverte || candidature.status === 'Accepted') && (
                    <LienBouton to={`/messages?annonce=${candidature.listingId}`} apparence="secondaire" taille="sm">
                      {t.candidatureConversation}
                    </LienBouton>
                  )}

                  {peutProposerUneVisite(candidature.status) && candidature.listingId !== null && (
                    <Bouton
                      apparence={candidature.status === 'VisitScheduled' ? 'discret' : 'secondaire'}
                      taille="sm"
                      onClick={() => setVisiteOuverte(visiteOuverte === candidature.id ? null : candidature.id)}
                    >
                      {t.proposerVisite}
                    </Bouton>
                  )}

                  {candidature.status === 'VisitScheduled' && (
                    <LienBouton to="/visites" taille="sm">
                      {t.candidatureVoirVisite}
                    </LienBouton>
                  )}

                  {candidature.status === 'ConvertedToLease' && (
                    <LienBouton to="/mes-baux" taille="sm">
                      {t.mesBaux}
                    </LienBouton>
                  )}

                  {ouverte && (
                    <Bouton
                      apparence="discret"
                      taille="sm"
                      charge={occupe === candidature.id}
                      onClick={() => void retirer(candidature.id)}
                    >
                      {t.candidatureRetirer}
                    </Bouton>
                  )}
                </div>

                {/*
                  C'est le demandeur qui propose ses créneaux, depuis sa propre candidature : la visite
                  se rattache ainsi au dossier, et sa confirmation fait passer la candidature en
                  « visite prévue » sans qu'on ait à les relier après coup.
                */}
                {visiteOuverte === candidature.id && candidature.listingId !== null && (
                  <ProposerVisite
                    langue={langue}
                    annonceId={candidature.listingId}
                    candidatureId={candidature.id}
                    onPropose={() => {
                      setVisiteOuverte(null)
                      onChange()
                    }}
                  />
                )}
              </div>
            </div>
          </li>
        )
      })}
    </ul>
  )
}

/**
 * Le fil d'étapes d'une candidature ouverte : déposée → vote du foyer → visite → décision.
 *
 * Le vote n'a pas lieu pour toutes les annonces ; l'étape reste, cochée, quand la candidature l'a
 * dépassée sans passer par lui — le fil dit où l'on est, pas tout ce qui s'est passé.
 */
function Etapes({ t, statut }: { t: Textes; statut: StatutCandidature }) {
  const rang = RANG_ETAPE[statut] ?? 1
  const etapes = [t.statutSubmitted, t.candidatureEtapeVote, t.candidatureEtapeVisite, t.candidatureEtapeDecision]

  return (
    <ol className="etapes candidature__etapes">
      {etapes.map((libelle, index) => {
        const numero = index + 1
        const classe = numero < rang ? 'est-faite' : numero === rang ? 'est-active' : undefined

        return (
          <li key={libelle} className={classe}>
            <span className="etapes__puce">{numero < rang ? '✓' : numero}</span> {libelle}
          </li>
        )
      })}
    </ol>
  )
}

/** L'étape active, pour chaque état ouvert. Déposée = 1, ce qui suit avance. */
const RANG_ETAPE: Partial<Record<StatutCandidature, number>> = {
  Submitted: 2,
  UnderHouseholdVote: 2,
  Shortlisted: 3,
  VisitScheduled: 4,
}

// ---------------------------------------------------------------------------
// Côté propriétaire
// ---------------------------------------------------------------------------

function Recues({
  t,
  langue,
  liste,
  onChange,
  onErreur,
}: {
  t: Textes
  langue: Langue
  liste: Candidature[]
  onChange: () => void
  onErreur: (message: string) => void
}) {
  const navigate = useNavigate()
  const [refus, setRefus] = useState<Candidature | null>(null)
  const [occupe, setOccupe] = useState<string | null>(null)

  const agir = async (id: string, action: () => Promise<void>) => {
    setOccupe(id)

    try {
      await action()
      setRefus(null)
      onChange()
    } catch (cause: unknown) {
      onErreur(messageErreur(langue, cause instanceof ErreurApi ? cause.code : 'internal'))
    } finally {
      setOccupe(null)
    }
  }

  if (liste.length === 0) {
    return <EtatVide icone="fichier" titre={t.candidaturesRecuesAucune} />
  }

  const pourcent = new Intl.NumberFormat(langue, { maximumFractionDigits: 0 })

  return (
    <div className="pile">
      <Bandeau ton="info" icone={<Icone nom="info" />}>
        {t.candidaturesRecuesAide}
      </Bandeau>

      <div className="table-defilante">
        <table>
          <caption className="visuellement-cachee">{t.candidaturesRecues}</caption>
          <thead>
            <tr>
              <th scope="col">{t.candidatureCandidat}</th>
              <th scope="col">{t.candidatureAnnonce}</th>
              <th scope="col">{t.candidatureRevenu}</th>
              <th scope="col">{t.dossierGarant}</th>
              <th scope="col">{t.candidatureScore}</th>
              <th scope="col">{t.candidatureEtat}</th>
              <th scope="col">{t.echeanceAction}</th>
            </tr>
          </thead>
          <tbody>
            {liste.map((candidature) => {
              const ouverte = estOuverte(candidature.status)
              const close = !ouverte && candidature.status !== 'Accepted' && candidature.status !== 'ConvertedToLease'

              return (
                <tr key={candidature.id} className={close ? 'est-estompee' : undefined}>
                  <td>
                    <span className="rangee rangee--serree candidature__candidat">
                      <span className="avatar avatar--sm" aria-hidden="true">
                        {initiales(candidature.seekerDisplayName)}
                      </span>
                      {candidature.seekerDisplayName ?? '—'}
                    </span>
                  </td>
                  <td className="secondaire">
                    {candidature.listingId !== null ? (
                      <Link to={`/annonces/${candidature.listingId}`}>{candidature.listingTitle ?? '—'}</Link>
                    ) : (
                      (candidature.listingTitle ?? '—')
                    )}
                  </td>
                  <td>{libelleTranche(t, candidature.seekerIncomeBand)}</td>
                  <td>{libelleGarant(t, candidature.seekerGuarantorType)}</td>
                  <td>
                    {candidature.matchScore === null ? (
                      <span className="secondaire">—</span>
                    ) : (
                      <Puce ton={candidature.matchScore >= 0.8 ? 'accent' : undefined}>
                        {pourcent.format(candidature.matchScore * 100)}
                      </Puce>
                    )}
                  </td>
                  <td>
                    <span className={`statut-point ${pointStatut(candidature.status)}`}>
                      {libelleStatut(t, candidature.status)}
                    </span>
                  </td>
                  <td>
                    {ouverte ? (
                      <span className="rangee rangee--serree candidature__gestes">
                        {candidature.status !== 'Shortlisted' && candidature.status !== 'VisitScheduled' && (
                          <Bouton
                            taille="sm"
                            charge={occupe === candidature.id}
                            onClick={() => void agir(candidature.id, () => preselectionner(candidature.id))}
                          >
                            {t.preselectionner}
                          </Bouton>
                        )}
                        <Bouton
                          apparence="secondaire"
                          taille="sm"
                          charge={occupe === candidature.id}
                          onClick={() => void agir(candidature.id, () => accepter(candidature.id))}
                        >
                          {t.accepter}
                        </Bouton>
                        <Bouton apparence="discret" taille="sm" desactive={occupe !== null} onClick={() => setRefus(candidature)}>
                          {t.candidatureRefuserMotif}
                        </Bouton>
                      </span>
                    ) : candidature.status === 'Accepted' || candidature.status === 'ConvertedToLease' ? (
                      <button type="button" className="lien" onClick={() => navigate('/mes-baux')}>
                        {t.echeancierBail}
                      </button>
                    ) : (
                      <span className="secondaire">—</span>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {refus !== null && (
        <Refus
          t={t}
          candidature={refus}
          occupe={occupe === refus.id}
          onFermer={() => setRefus(null)}
          onRefuser={(motif, note) => void agir(refus.id, () => refuser(refus.id, motif, note))}
        />
      )}
    </div>
  )
}

/** Le refus, en feuille : un motif dans une liste fermée, une note facultative. */
function Refus({
  t,
  candidature,
  occupe,
  onFermer,
  onRefuser,
}: {
  t: Textes
  candidature: Candidature
  occupe: boolean
  onFermer: () => void
  onRefuser: (motif: Motif, note: string | null) => void
}) {
  const [motif, setMotif] = useState<Motif>('already_rented')
  const [note, setNote] = useState('')

  return (
    <Modale
      feuille
      titre={interpoler(t.candidatureRefuserTitre, { nom: candidature.seekerDisplayName ?? '—' })}
      onFermer={onFermer}
      pied={
        <>
          <Bouton apparence="discret" onClick={onFermer}>
            {t.annuler}
          </Bouton>
          <Bouton apparence="danger" charge={occupe} onClick={() => onRefuser(motif, note.trim() === '' ? null : note.trim())}>
            {t.refuser}
          </Bouton>
        </>
      }
    >
      <Liste
        id="refus-motif"
        libelle={t.motifRefus}
        valeur={motif}
        options={[
          { valeur: 'already_rented', libelle: t.motifDejaLoue },
          { valeur: 'budget', libelle: t.motifBudget },
          { valeur: 'dates', libelle: t.motifDates },
          { valeur: 'other', libelle: t.motifAutre },
        ]}
        onChange={(valeur) => setMotif(valeur as Motif)}
      />
      <Zone id="refus-note" libelle={t.candidatureRefusNote} valeur={note} onChange={setNote} aide={t.candidatureRefusAide} />
    </Modale>
  )
}

/** Les candidatures reçues sur UNE annonce, sous sa fiche — même tableau, sans la colonne annonce. */
export function CandidaturesRecues({ langue, annonceId }: { langue: Langue; annonceId: string }) {
  const t = TEXTES[langue]
  const [liste, setListe] = useState<Candidature[] | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)

  const charger = useCallback(() => {
    void candidaturesRecues(annonceId)
      .then((page) => setListe(page.items))
      .catch((cause: unknown) => {
        setListe([])
        setErreur(messageErreur(langue, cause instanceof ErreurApi ? cause.code : 'internal'))
      })
  }, [annonceId, langue])

  useEffect(charger, [charger])

  return (
    <section className="candidatures">
      <h3>{t.candidaturesRecues}</h3>
      {erreur !== null && <Bandeau ton="erreur">{erreur}</Bandeau>}
      {liste === null && <SqueletteListe />}
      {liste !== null && liste.length === 0 && erreur === null && (
        <EtatVide icone="fichier" titre={t.candidaturesRecuesVide} />
      )}
      {liste !== null && liste.length > 0 && (
        <Recues t={t} langue={langue} liste={liste} onChange={charger} onErreur={setErreur} />
      )}
    </section>
  )
}

/**
 * Le formulaire de candidature, affiché sous une fiche d'annonce.
 *
 * Le dossier doit être complet : l'API le refuse sinon, et le message renvoyé dit exactement quoi
 * faire. On ne duplique pas ce contrôle ici — deux implémentations de la même règle divergent.
 */
export function FormulaireCandidature({
  langue,
  annonceId,
  onDepose,
}: {
  langue: Langue
  annonceId: string
  onDepose?: () => void
}) {
  const t = TEXTES[langue]
  const navigate = useNavigate()

  const [motivation, setMotivation] = useState('')
  const [envoi, setEnvoi] = useState(false)
  const [erreur, setErreur] = useState<string | null>(null)
  const [dossierManquant, setDossierManquant] = useState(false)

  const soumettre = useCallback(async () => {
    if (envoi) {
      return
    }

    setEnvoi(true)
    setErreur(null)

    try {
      await candidater(annonceId, motivation || null)
      onDepose?.()
      navigate('/mes-candidatures')
    } catch (cause: unknown) {
      const code = cause instanceof ErreurApi ? cause.code : 'internal'

      // Un refus pour dossier incomplet doit MENER au dossier. Dire « completez votre dossier »
      // sans donner le chemin transforme un refus explicable en cul-de-sac.
      setDossierManquant(code.startsWith('seeking.application.profile'))
      setErreur(messageErreur(langue, code))
    } finally {
      setEnvoi(false)
    }
  }, [annonceId, envoi, langue, motivation, navigate, onDepose])

  return (
    <form
      className="candidater"
      onSubmit={(evenement) => {
        evenement.preventDefault()
        void soumettre()
      }}
    >
      {erreur !== null && (
        <Bandeau ton="erreur">
          {erreur}
          {dossierManquant && (
            <>
              {' '}
              <Link to="/mon-dossier">{t.monDossier}</Link>
            </>
          )}
        </Bandeau>
      )}

      <Zone id="motivation" libelle={t.motivation} valeur={motivation} onChange={setMotivation} />

      <Bouton type="submit" charge={envoi}>
        {t.candidater}
      </Bouton>
    </form>
  )
}

// ---------------------------------------------------------------------------
// Libellés
// ---------------------------------------------------------------------------

/** Proposer une visite vaut aussi après acceptation — voir le commentaire de l'écran. */
function peutProposerUneVisite(statut: StatutCandidature): boolean {
  return (estOuverte(statut) && statut !== 'VisitScheduled') || statut === 'Accepted'
}

function estOuverte(statut: StatutCandidature): boolean {
  return (
    statut === 'Submitted' ||
    statut === 'Shortlisted' ||
    statut === 'UnderHouseholdVote' ||
    statut === 'VisitScheduled'
  )
}

function tonStatut(statut: StatutCandidature): 'succes' | 'attention' | 'danger' | 'info' | undefined {
  switch (statut) {
    case 'Accepted':
    case 'ConvertedToLease':
    case 'VisitScheduled':
      return 'succes'
    case 'UnderHouseholdVote':
      return 'attention'
    case 'Shortlisted':
      return 'info'
    case 'Rejected':
      return 'danger'
    default:
      return undefined
  }
}

function pointStatut(statut: StatutCandidature): string {
  switch (statut) {
    case 'Accepted':
    case 'ConvertedToLease':
    case 'VisitScheduled':
      return 'statut-point--succes'
    case 'UnderHouseholdVote':
      return 'statut-point--attente'
    case 'Shortlisted':
      return 'statut-point--info'
    case 'Rejected':
      return 'statut-point--danger'
    default:
      return ''
  }
}

function libelleStatut(t: Textes, statut: StatutCandidature): string {
  switch (statut) {
    case 'Draft':
      return t.statutDraft
    case 'Submitted':
      return t.statutSubmitted
    case 'UnderHouseholdVote':
      return t.statutUnderHouseholdVote
    case 'Shortlisted':
      return t.statutShortlisted
    case 'VisitScheduled':
      return t.statutVisitScheduled
    case 'Accepted':
      return t.statutAccepted
    case 'Rejected':
      return t.statutRejected
    case 'Withdrawn':
      return t.statutWithdrawn
    case 'Expired':
      return t.statutExpired
    case 'ConvertedToLease':
      return t.statutConvertedToLease
  }
}

function libelleMotif(t: Textes, code: string | null): string {
  switch (code) {
    case 'already_rented':
      return t.motifDejaLoue
    case 'budget':
      return t.motifBudget
    case 'dates':
      return t.motifDates
    case 'other':
      return t.motifAutre
    default:
      return code ?? '—'
  }
}

function libelleTranche(t: Textes, tranche: TrancheRevenu | null): string {
  switch (tranche) {
    case 'UnderThreeThousand':
      return t.revenuUnderThreeThousand
    case 'ThreeToFiveThousand':
      return t.revenuThreeToFiveThousand
    case 'FiveToEightThousand':
      return t.revenuFiveToEightThousand
    case 'EightToTwelveThousand':
      return t.revenuEightToTwelveThousand
    case 'OverTwelveThousand':
      return t.revenuOverTwelveThousand
    default:
      // « Je préfère ne pas le dire » est la phrase du demandeur ; le propriétaire lit un fait.
      return t.candidatureNonCommunique
  }
}

function libelleGarant(t: Textes, garant: TypeGarant | null): string {
  switch (garant) {
    case 'Family':
      return t.garantFamily
    case 'Employer':
      return t.garantEmployer
    case 'Institution':
      return t.garantInstitution
    case 'Other':
      return t.garantOther
    default:
      return t.candidatureAucunGarant
  }
}

/** Deux lettres pour l'avatar : « Youssef Amrani » → « YA ». */
function initiales(nom: string | null): string {
  if (nom === null) return '?'

  return nom
    .split(/\s+/)
    .filter((mot) => mot.length > 0)
    .slice(0, 2)
    .map((mot) => mot.charAt(0).toLocaleUpperCase())
    .join('')
}
